import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ConfigProvider, theme } from 'antd';
import { Provider } from 'react-redux';
import { QueryClientProvider, QueryClient } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import AppLayout from './components/common/AppLayout';
import LoginPage from './pages/LoginPage';
import NotificationProvider from './components/common/NotificationProvider';
import RTLProvider from './components/common/RTLProvider';
import store from './store';
import { API_BASE } from './api/config';
import './i18n/config';
import './App.css';
import { useIsRTL } from './utils/rtl';

// Create a client for React Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1
    }
  }
});

// Make queryClient available globally for components that need it
declare global {
  interface Window {
    queryClient: QueryClient;
  }
}
window.queryClient = queryClient;

// Auth guard component
const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const [isAuthenticated, setIsAuthenticated] = React.useState<boolean | null>(null);
  const [isLoading, setIsLoading] = React.useState(true);

  React.useEffect(() => {
    // Check authentication status using the API
    const checkAuth = async () => {
      try {
        // First check if we have a token in localStorage
        const token = localStorage.getItem('dashboard_token');

        // If we have a token in localStorage, set it as a cookie
        if (token) {
          document.cookie = `dashboard_token=${token}; path=/; max-age=${8 * 60 * 60}`;
          console.log('Set cookie from localStorage token');
        }

        // Use the configured API_BASE instead of hardcoded localhost
        const cacheBuster = new Date().getTime();
        // Use the token we already retrieved above
        const response = await fetch(`${API_BASE}/dashboard/check-auth?_=${cacheBuster}`, {
          credentials: 'include',
          headers: {
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            'Pragma': 'no-cache',
            'Expires': '0',
            'X-Requested-With': 'XMLHttpRequest',
            'Authorization': token ? `Bearer ${token}` : '',
            'Dashboard-Authorization': token ? `Bearer ${token}` : ''
          },
          mode: 'cors'
        });
        const result = await response.json();

        console.log('Authentication check result:', result);
        setIsAuthenticated(result.authenticated);
        setIsLoading(false);
      } catch (error) {
        console.error('Error checking authentication:', error);
        setIsAuthenticated(false);
        setIsLoading(false);
      }
    };

    checkAuth();
  }, []);

  // Show loading indicator while checking authentication
  if (isLoading) {
    return <div>Loading...</div>;
  }

  if (!isAuthenticated) {
    // Redirect to login if not authenticated
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

// App content with theme configuration
const AppContent = () => {
  const isRTL = useIsRTL();

  // Configure theme based on language direction
  const themeConfig = {
    algorithm: theme.defaultAlgorithm,
    token: {
      colorPrimary: '#1890ff',
      borderRadius: 4,
    },
    direction: isRTL ? 'rtl' : 'ltr',
    components: {
      Layout: {
        bodyBg: '#f0f2f5',
      },
    },
  };

  return (
    <RTLProvider>
      <ConfigProvider theme={themeConfig} direction={isRTL ? 'rtl' : 'ltr'}>
        <NotificationProvider>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route
              path="/*"
              element={
                <ProtectedRoute>
                  <AppLayout />
                </ProtectedRoute>
              }
            />
          </Routes>
        </NotificationProvider>
      </ConfigProvider>
    </RTLProvider>
  );
};

function App() {
  return (
    <Provider store={store}>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <AppContent />
        </BrowserRouter>
        <ReactQueryDevtools initialIsOpen={false} />
      </QueryClientProvider>
    </Provider>
  );
}

export default App;
